/**
 * Modelos del sistema de notificaciones del TMS.
 * El contrato REST lo implementa el backend en paralelo: NO cambiar las formas.
 */

export interface NotificationDTO {
  id: number;
  type: string;
  params: Record<string, string>;
  link: string | null;
  seen: boolean;
  createdAt: string;
  actorName: string | null;
}

export interface NotificationPreference {
  type: string;
  muted: boolean;
  soundMuted: boolean;
  emailEnabled: boolean;
  toastEnabled: boolean;
}

export interface NotificationPollResponse {
  unseenCount: number;
  latestId: number | null;
  events: NotificationDTO[];
}

export interface NotificationListResponse {
  items: NotificationDTO[];
  total: number;
}

/** Metadatos de presentación de cada tipo: icono Material + color de acento. */
export interface NotificationTypeMeta {
  type: string;
  icon: string;
  color: string;
}

/** Orden canónico: se usa en filtros del panel y en la página de preferencias. */
export const NOTIFICATION_TYPES: NotificationTypeMeta[] = [
  { type: 'SUGGESTION_CREATED', icon: 'lightbulb', color: '#f59e0b' },
  { type: 'SUGGESTION_DECIDED', icon: 'thumbs_up_down', color: '#16a34a' },
  { type: 'SUGGESTION_COMMENT', icon: 'forum', color: '#0ea5e9' },
  { type: 'PARTICIPANT_CHANGED', icon: 'group', color: '#29abe2' },
  { type: 'TASK_STATUS_CHANGED', icon: 'swap_horiz', color: '#8b5cf6' },
  { type: 'TASK_COMMENT', icon: 'chat_bubble', color: '#10b981' },
  { type: 'QA_FEEDBACK', icon: 'bug_report', color: '#ef4444' },
  { type: 'TASK_EDITED', icon: 'edit', color: '#1b1464' }
];

const TYPE_META_MAP = new Map(NOTIFICATION_TYPES.map(m => [m.type, m]));

export function metaFor(type: string): NotificationTypeMeta {
  return TYPE_META_MAP.get(type) ?? { type, icon: 'notifications', color: '#6b7280' };
}

/** Valores por defecto de las preferencias cuando el servidor aún no tiene fila para el tipo. */
export function defaultPreference(type: string): NotificationPreference {
  return { type, muted: false, soundMuted: false, emailEnabled: true, toastEnabled: true };
}

/**
 * Pequeña utilidad de interpolación sobre strings traducidos:
 * "Hola {name}" + { name: 'Ana' } → "Hola Ana".
 * Los placeholders sin valor se sustituyen por cadena vacía.
 */
export function interpolate(template: string, params: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_match, key: string) => params[key] ?? '');
}
